import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import BottomAction from "@fuse/sharedComponents/buttons/BottomAction"
import { InstrestedEmailCapture } from "@fuse/sharedComponents/instrestedEmailCapture"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import MapWebView from "@fuse/sharedComponents/map"
import { showInfoToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  EFeatureFlags,
  EVENT_CLASS,
  EventDocData,
  TEventDetail,
} from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import { ROUTES } from "src/App"

import momentTz from "@settingsAlias/momentInstance"

import { increaseInterestedCountApi } from "@api/firebaseWrappedApis"

import BuddiesGuestList from "@fuse/pages/eventPage/components/BuddiesGuestList"
import { WhichBtnState } from "@fuse/pages/eventPage/controller"
import RenderEventDescription, {
  RenderEventDescriptionViaHtml,
} from "@fuse/pages/eventPage/RenderEventDescription"
import DownloadAppModal, {
  DownloadAppModalHandle,
} from "@fuse/pages/landingPage/components/DownloadAppModal"

import First from "@fuse/assets/images/img-event-first.svg"

const PreviewDetail = ({
  eventDetails,
  eventDocData,
  myEventActions,
  onLeaveEvent,
  canLeaveEvent,
  acceptedGuestAvatars,
  onEventJoin,
  guestList,
  flyerImage,
  invitedId,
  onEventAction,
  isLoggedIn,
  myJoinStatus,
}: {
  eventDetails?: Partial<TEventDetail> | null
  eventDocData?: Partial<EventDocData>
  myEventActions?: any[]
  onLeaveEvent?: () => void
  canLeaveEvent?: boolean
  myTicketStatus?: string
  eventStatus?: string
  acceptedGuestAvatars?: any[]
  onEventJoin?: () => void
  guestList?: any[]
  flyerImage?: string
  invitedId?: string
  onEventAction?: (index: number) => void
  isLoggedIn: boolean
  myJoinStatus?: WhichBtnState
}) => {
  const eventDescription = get(eventDocData, "eventDescription", "")
  const eventDescriptionViaHtml = get(
    eventDocData,
    "event_description_via_html",
    "",
  )
  const params = useParams()
  const navigate = useNavigate()

  const eventId = get(params, "eventId", "")
  const displayTimezone = get(
    eventDocData,
    "timing.displayTimezone",
    "America/New_York",
  )
  const eventDate = momentTz(get(eventDocData, "timing.eventDate", new Date()))
    .tz(displayTimezone)
    .format("ddd, MMM DD YYYY")

  const eventEndDate = momentTz(
    get(eventDocData, "timing.eventEndDate", new Date()),
  )
    .tz(displayTimezone)
    .format("ddd, MMM DD YYYY")

  const eventStartTime = momentTz(
    get(eventDocData, "timing.eventStartTime", new Date()),
  )
    .tz(displayTimezone)
    .format("LT")

  const eventEndTime = momentTz(
    get(eventDocData, "timing.eventEndTime", new Date()),
  )
    .tz(displayTimezone)
    .format("LT")

  const signUpCloseDate = momentTz(
    get(eventDocData, "timing.signupCloseDate", new Date()),
  )
    .tz(displayTimezone)
    .format("MMM DD YYYY")

  const signUpCloseTime = momentTz(
    get(eventDocData, "timing.signupCloseTime", new Date()),
  )
    .tz(displayTimezone)
    .format("LT")

  const EventLocation = get(eventDocData, "location.eventLocation", "-")

  const otherLocationDetails = get(
    eventDocData,
    "location.otherLocationDetails",
    "",
  )

  // const flyerImage = get(eventDocData, "flyerImage", "")
  const totalTicketQuantity = get(eventDetails, "totalTicketQuantity", 0)
  const totalPurchasedTickets = get(eventDetails, "totalPurchasedTickets", 0)
  const ticketLeftString = `${totalTicketQuantity - totalPurchasedTickets} tickets left`
  const minTicketPrice = get(eventDetails, "minTicketPrice", 0)
  const maxTicketPrice = get(eventDetails, "maxTicketPrice", 0)

  const locationInfo = `${EventLocation} ${otherLocationDetails && `| ${otherLocationDetails}`}`

  const isSingleTicket = minTicketPrice === maxTicketPrice

  const eventClass = get(
    eventDocData,
    "eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  ) as EVENT_CLASS
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT

  const eventLocation = get(eventDocData, "location", "-")
  const latitude =
    typeof eventLocation === "string" ? 0 : eventLocation.latitude
  const longitude =
    typeof eventLocation === "string" ? 0 : eventLocation.longitude

  const openGoogleCalendar = () => {
    const baseUrl = "https://calendar.google.com/calendar/render"
    const title = get(eventDocData, "eventName", "-")
    const startDate = get(
      eventDocData,
      "timing.eventStartTime",
      new Date().toDateString(),
    ) // Format: YYYYMMDDTHHMMSSZ (UTC)
    const endDate = get(
      eventDocData,
      "timing.eventEndTime",
      new Date().toDateString(),
    ) // Format: YYYYMMDDTHHMMSSZ (UTC)

    const formatDateForCalendar = (dateString: string) => {
      const date = new Date(dateString)
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
    }

    const formattedStartDate = formatDateForCalendar(startDate)
    const formattedEndDate = formatDateForCalendar(endDate)

    // Construct the URL with query parameters
    const url = `${baseUrl}?action=TEMPLATE&text=${encodeURIComponent(
      title,
    )}&details=${encodeURIComponent(eventDescription)}&location=${encodeURIComponent(
      EventLocation,
    )}&dates=${formattedStartDate}/${formattedEndDate}`

    // Open the URL in a new tab
    window.open(url)
  }

  const [isInterested, setIsInterested] = useState(false)
  const [isInterestedLoading, setIsInterestedLoading] = useState(false)
  const [isShowEmailPopup, setIsShowEmailPopup] = useState(false)

  const handleInterestedClick = async () => {
    try {
      setIsInterestedLoading(true)
      await increaseInterestedCountApi({ eventId })
      setIsShowEmailPopup(true)
      setIsInterested(true)
      localStorage.setItem(`interested_${eventId}`, "true")
    } catch (error) {
      console.error("Error marking interest:", error)
    } finally {
      setIsInterestedLoading(false)
    }
  }
  const [isOpen, setIsOpen] = useState(false)

  const navigateToChooseTicket = () => {
    if (totalTicketQuantity - totalPurchasedTickets <= 0) {
      showInfoToast("No tickets left. Event is sold out!")
      return
    }
    if (!eventDetails?.eventDocData?.showSoldOut) {
      navigate(ROUTES.CHOOSE_TICKET.replace(":eventId", eventId), {
        state: { invitedId },
      })
    }
  }

  const isLocationHidden =
    (get(eventDocData, "location.guestLocationPreview", "Hidden") ||
      "Hidden") === "Hidden"

  useEffect(() => {
    const isInterested = localStorage.getItem(`interested_${eventId}`)
    setIsInterested(isInterested === "true")
  }, [eventId])

  const modalRef = useRef<DownloadAppModalHandle>(null)

  const handleButtonClick = () => {
    modalRef.current?.openModal()
  }

  return (
    <>
      <DownloadAppModal ref={modalRef} />

      {isShowEmailPopup && (
        <InstrestedEmailCapture onClose={() => setIsShowEmailPopup(false)} />
      )}
      {isOpen && (
        <BuddiesGuestList
          onClose={() => setIsOpen(false)}
          onJoinEvent={onEventJoin}
          hasJoined={true} // hasJoined
          guestList={guestList}
          acceptedGuestAvatars={acceptedGuestAvatars}
          isTicketedEvent={isTicketedEvent}
          myJoinStatus={myJoinStatus}
        />
      )}
      {isInterestedLoading && <LoadingSpinner />}

      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-[100vh] w-screen h-full flex flex-col overflow-auto"
      >
        <div className="flex justify-center pt-[12vh] md:mt-0 lg:mt-[74px] lg:p-4 md:mb-[74px] mb-1 sm:mb-[50px] overflow-hidden">
          <div className="flex-1 max-w-screen-xl">
            <div className="flex rounded-xl md:p-8 lg:p-12 flex-col lg:flex-row flex-wrap ">
              <div className="w-6/12 mx-auto lg:pr-6 mb-6 lg:mb-0 ">
                <div className=" px-4  md:max-w-md lg:max-w-lg ">
                  {!isEmpty(flyerImage) ? (
                    <img
                      src={flyerImage}
                      className=" w-full rounded-2xl"
                      alt="First"
                    />
                  ) : (
                    <img src={First} className=" w-full lg:p-0" alt="First" />
                  )}
                </div>
              </div>
              <div className="flex-1">
                <div className="px-5 pb-5">
                  <div className=" flex gap-4 items-center">
                    <h5 className="text-3xl md:text-4xl text-start text-white ">
                      {get(eventDocData, "eventName", "-")}
                    </h5>
                  </div>
                  {isLoggedIn ? (
                    <div className="flex flex-row mt-2 items-center">
                      <div className="h-6 w-6 rounded-full bg-gray-400 overflow-hidden mr-2 sm:hidden">
                        <img
                          src={get(eventDocData, "initiatorAvatarUrl", "")}
                          alt="profile"
                          className="w-full h-full"
                        />
                      </div>
                      <h5 className="text-start text-white/35">
                        by {get(eventDocData, "initiatorName", "-")}
                      </h5>
                    </div>
                  ) : null}

                  {
                    <div className="mt-4 flex items-center justify-between text-white/80 rounded-xl px-4 py-2 w-fit max-w-md shadow-lg border border-white/10 bg-white/10 sm:bg-transparent">
                      <div className="flex items-center space-x-2">
                        <span>🛠️</span>
                        <p className="text-sm text-start ">
                          Some features are being updated on the web. For full
                          access, use our app.
                        </p>
                      </div>
                      <button
                        onClick={handleButtonClick}
                        className="ml-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  }

                  <div className="my-6 bg-white/10 border-white/20 border px-4 py-3 rounded-2xl sm:bg-transparent sm:border-0 sm:px-0 sm:py-0 sm:rounded-none">
                    <div className="flex flex-row mb-6 ">
                      <div className="flex text-center items-center">
                        <p className="text-3xl">🗓️</p>
                      </div>
                      <div className="flex-col ml-3">
                        <h5 className="text-start text-white">
                          {eventDate} - {eventEndDate}
                        </h5>

                        <h5 className="text-start text-white/35 text-sm font-medium">
                          {eventStartTime} - {eventEndTime}
                        </h5>
                      </div>
                    </div>

                    <div className={`flex flex-row mb-6 `}>
                      <div className="flex text-center items-center">
                        <p className="text-2xl">📍</p>
                      </div>

                      <div className="flex-col ml-4">
                        <h5 className="text-start text-white">
                          {get(
                            eventDocData,
                            "location.guestLocationPreview",
                            "Hidden",
                          ) || "Hidden"}
                        </h5>
                        <h5 className="text-start text-white/35 text-sm font-medium">
                          {locationInfo}
                        </h5>
                      </div>
                    </div>

                    {isTicketedEvent && (
                      // <div className="flex flex-row items-center">
                      <div
                        className="flex flex-row mb-6 cursor-pointer"
                        // onClick={navigateToChooseTicket}
                      >
                        <div className="flex text-center items-center">
                          <p className="text-3xl">️🎟️</p>
                        </div>
                        <div className="flex-col ml-4">
                          <h5 className="text-start text-white">
                            {ticketLeftString}
                          </h5>
                        </div>
                      </div>
                    )}

                    {!isTicketedEvent && (
                      <div className="flex flex-row mb-6">
                        <div className="flex text-center items-center">
                          <p className="text-3xl">✅</p>
                        </div>
                        <div className="flex-col ml-3">
                          <h5 className="text-start text-white">{`X joined`}</h5>
                          <h5 className="text-start text-white/35 text-sm">
                            Sign up close on {signUpCloseDate} at{" "}
                            {signUpCloseTime}
                          </h5>
                        </div>
                      </div>
                    )}

                    {isTicketedEvent && (
                      <div
                        className="flex flex-row items-center"
                        onClick={navigateToChooseTicket}
                      >
                        <div className="flex text-center items-center">
                          <p className="text-3xl">💵</p>
                        </div>
                        <div className="flex-col ml-4 items-center">
                          <h5 className="text-center text-white">
                            {isSingleTicket
                              ? `$${minTicketPrice}`
                              : `$${minTicketPrice} - $${maxTicketPrice}`}
                          </h5>
                        </div>
                      </div>
                    )}

                    {canLeaveEvent && (
                      <div
                        className="flex flex-row cursor-pointer mt-6"
                        onClick={() => onLeaveEvent()}
                      >
                        <div className="flex text-center items-center">
                          <p className="text-3xl">👋</p>
                        </div>
                        <div className="flex-col ml-4">
                          <h5 className="text-start text-white underline">
                            Leave event
                          </h5>
                          <h5 className="text-start text-white/35 text-sm">
                            Click here to leave event
                          </h5>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-col-reverse">
                    <div className="bg-white/10 border-white/20 border px-4 py-3 rounded-2xl sm:bg-transparent sm:border-0 sm:px-0 sm:py-0 sm:rounded-none">
                      <h6 className="text-lg sm:text-xl text-start text-white/35 sm:text-white">
                        About this event
                      </h6>
                      {eventDescriptionViaHtml ? (
                        <RenderEventDescriptionViaHtml
                          eventDescriptionViaHtml={eventDescriptionViaHtml}
                        />
                      ) : (
                        <h6 className="text-md text-start text-white/80 sm:text-white/35 mt-1">
                          <div>
                            <RenderEventDescription
                              eventDescription={eventDescription}
                            />
                          </div>
                        </h6>
                      )}
                    </div>
                  </div>

                  {
                    <MapWebView
                      isEventDetailView={true}
                      coordinate={{
                        lat: eventDocData?.location?.latitude,
                        lng: eventDocData?.location?.longitude,
                      }}
                    />
                  }
                  <div className="portrait:hidden ">
                    {!isEmpty(myEventActions) && (
                      <BottomAction
                        bottomActionClassName="mt-6"
                        buttonOptionList={myEventActions}
                        handleScreen={onEventAction}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BlurWrapper>
    </>
  )
}

export default PreviewDetail

const Tag = ({ title }: { title: string }) => (
  <div className="bg-[red] text-white rounded-full py-2 px-4 text-sm font-semibold ">
    {title}
  </div>
)
